import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Flame, Lock, LogOut, Inbox, Mail, Phone, MapPin, Trash2, Check,
  Search, RefreshCw, CircleDot,
} from "lucide-react";
import { api, API, setAuthToken } from "../api";
import { toast } from "../hooks/use-toast";
import { Input } from "../components/ui/input";

const TOKEN_KEY = "ss_admin_token";

const Admin = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (token) {
      setAuthToken(token);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setAuthToken(data.token);
      setToken(data.token);
      toast({ title: "Welcome back", description: "You are signed in to the enquiry inbox." });
    } catch (err) {
      toast({ title: "Incorrect password", description: "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthToken(null);
    setToken("");
    setEnquiries([]);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (item) => {
    const status = item.status === "new" ? "read" : "new";
    try {
      await api.patch(`/enquiries/${item.id}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.id === item.id ? { ...e, status } : e)));
      if (selected?.id === item.id) setSelected({ ...selected, status });
    } catch (_) {}
  };

  const remove = async (item) => {
    try {
      await api.delete(`/enquiries/${item.id}`);
      setEnquiries((prev) => prev.filter((e) => e.id !== item.id));
      if (selected?.id === item.id) setSelected(null);
      toast({ title: "Enquiry deleted" });
    } catch (_) {}
  };

  const filtered = useMemo(() => {
    return enquiries.filter((e) => {
      const matchQ = !query || [e.name, e.email, e.phone, e.town, e.postcode]
        .join(" ").toLowerCase().includes(query.toLowerCase());
      const matchF = filter === "all" || e.status === filter;
      return matchQ && matchF;
    });
  }, [enquiries, query, filter]);

  const newCount = enquiries.filter((e) => e.status === "new").length;

  const fmt = (d) => {
    try {
      return new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return d; }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-charcoal px-5" style={{ backgroundColor: "#343434" }}>
        <form onSubmit={login} className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-2xl">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="w-11 h-11 rounded-md bg-fire flex items-center justify-center"><Flame className="text-white" size={22} /></span>
            <div className="leading-none">
              <p className="font-head font-bold uppercase text-charcoal text-lg">Stove Scotland</p>
              <p className="text-fire text-xs font-medium">Enquiry Inbox</p>
            </div>
          </div>
          <h1 className="font-head text-2xl font-bold uppercase text-charcoal flex items-center gap-2 mb-2"><Lock size={20} /> Admin Login</h1>
          <p className="text-gray-500 text-sm mb-5">Enter the shared password to view enquiries.</p>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoFocus />
          <button type="submit" disabled={loading} className="btn-fire w-full mt-5 py-3 rounded-md font-semibold disabled:opacity-60">
            {loading ? "Signing in..." : "Sign In"}
          </button>
          <button type="button" onClick={() => navigate("/")} className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-fire transition-colors">
            Back to website
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-charcoal text-white" style={{ backgroundColor: "#343434" }}>
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-md bg-fire flex items-center justify-center"><Flame className="text-white" size={20} /></span>
            <div className="leading-none">
              <p className="font-head font-bold uppercase text-lg">Enquiry Inbox</p>
              <p className="text-white/50 text-xs">Stove Scotland admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="flex items-center gap-2 text-sm text-white/80 hover:text-white"><RefreshCw size={16} /> Refresh</button>
            <button onClick={logout} className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-3 py-2 rounded-md"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 py-8">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Stat icon={Inbox} label="Total" value={enquiries.length} />
          <Stat icon={CircleDot} label="New" value={newCount} accent />
          <Stat icon={Check} label="Read" value={enquiries.length - newCount} />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, phone, town..." className="w-full pl-10 pr-4 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-fire/40" />
          </div>
          <div className="flex gap-2">
            {["all", "new", "read"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2.5 rounded-md text-sm font-medium capitalize transition-colors ${filter === f ? "bg-fire text-white" : "bg-white text-gray-600 border border-gray-300 hover:border-fire"}`}>{f}</button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl p-16 text-center text-gray-400">
            <Inbox size={48} className="mx-auto mb-4 opacity-40" />
            <p>No enquiries {query || filter !== "all" ? "match your filter" : "yet"}.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl overflow-hidden shadow-sm divide-y divide-gray-100">
            {filtered.map((e) => (
              <div key={e.id} className="p-5 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <button onClick={() => setSelected(selected?.id === e.id ? null : e)} className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      {e.status === "new" && <span className="w-2 h-2 rounded-full bg-fire" />}
                      <span className="font-semibold text-charcoal">{e.name}</span>
                      <span className="text-gray-400 text-sm">{fmt(e.created_at)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5 text-sm text-gray-600">
                      <span className="flex items-center gap-1.5"><Phone size={14} className="text-fire" /> {e.phone}</span>
                      <span className="flex items-center gap-1.5"><Mail size={14} className="text-fire" /> {e.email}</span>
                      {e.town && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-fire" /> {e.town} {e.postcode}</span>}
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    <button onClick={() => markRead(e)} title={e.status === "new" ? "Mark as read" : "Mark as new"} className="p-2 rounded-md hover:bg-gray-100 text-gray-500"><Check size={18} /></button>
                    <button onClick={() => remove(e)} title="Delete" className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={18} /></button>
                  </div>
                </div>
                {selected?.id === e.id && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4 text-sm text-gray-700 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    <Detail label="Address" value={[e.address, e.town, e.postcode].filter(Boolean).join(", ") || "\u2014"} />
                    <Detail label="Heard via" value={e.hear || "\u2014"} />
                    <Detail label="Stove location photo" value={e.location || "None uploaded"} />
                    <div className="sm:col-span-2">
                      <Detail label="Message" value={e.message || "\u2014"} />
                    </div>
                    {e.photo_path && (
                      <div className="sm:col-span-2">
                        <a href={`${API}/enquiries/${e.id}/photo?auth=${token}`} target="_blank" rel="noreferrer" data-testid={`enquiry-photo-link-${e.id}`}>
                          <img src={`${API}/enquiries/${e.id}/photo?auth=${token}`} alt={`Stove location from ${e.name}`} className="max-h-72 rounded-md border border-gray-200 hover:opacity-90 transition-opacity" data-testid={`enquiry-photo-${e.id}`} />
                        </a>
                        <p className="text-xs text-gray-400 mt-1">Click photo to open full size</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl p-5 flex items-center gap-4 ${accent ? "bg-fire text-white" : "bg-white"}`}>
    <span className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent ? "bg-white/20" : "bg-fire/10"}`}>
      <Icon size={22} className={accent ? "text-white" : "text-fire"} />
    </span>
    <div>
      <p className={`text-2xl font-bold font-head ${accent ? "text-white" : "text-charcoal"}`}>{value}</p>
      <p className={`text-sm ${accent ? "text-white/80" : "text-gray-500"}`}>{label}</p>
    </div>
  </div>
);

const Detail = ({ label, value }) => (
  <p><span className="font-semibold text-charcoal">{label}: </span>{value}</p>
);

export default Admin;
